import { jsx as _jsx } from "react/jsx-runtime";
import { createContext, useContext, useState, useCallback } from 'react';
const NotificationContext = createContext(undefined);
export const useNotifications = () => {
    const context = useContext(NotificationContext);
    if (context === undefined) {
        throw new Error('useNotifications must be used within a NotificationProvider');
    }
    return context;
};
export const NotificationProvider = ({ children }) => {
    const [notifications, setNotifications] = useState([]);
    const addNotification = useCallback((notification) => {
        const newNotification = {
            ...notification,
            id: Date.now().toString() + Math.random().toString(36).substr(2, 9),
            createdAt: new Date(),
        };
        setNotifications(prev => [...prev, newNotification]);
        // Auto remove notification after duration
        if (notification.duration !== 0) {
            const duration = notification.duration || 5000; // Default 5 seconds
            setTimeout(() => {
                removeNotification(newNotification.id);
            }, duration);
        }
    }, []);
    const removeNotification = useCallback((id) => {
        setNotifications(prev => prev.filter(notification => notification.id !== id));
    }, []);
    const clearNotifications = useCallback(() => {
        setNotifications([]);
    }, []);
    const success = useCallback((title, message, duration) => {
        addNotification({
            type: 'success',
            title,
            message: message || '',
            duration,
        });
    }, [addNotification]);
    const error = useCallback((title, message, duration) => {
        addNotification({
            type: 'error',
            title,
            message: message || '',
            duration: duration || 8000, // Errors stay longer by default
        });
    }, [addNotification]);
    const warning = useCallback((title, message, duration) => {
        addNotification({
            type: 'warning',
            title,
            message: message || '',
            duration,
        });
    }, [addNotification]);
    const info = useCallback((title, message, duration) => {
        addNotification({
            type: 'info',
            title,
            message: message || '',
            duration,
        });
    }, [addNotification]);
    const value = {
        notifications,
        addNotification,
        removeNotification,
        clearNotifications,
        success,
        error,
        warning,
        info,
    };
    return (_jsx(NotificationContext.Provider, { value: value, children: children }));
};
